import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FaStar,
  FaStarHalfAlt,
  FaRegStar,
  FaShoppingBag,
  FaHeart,
  FaShare,
  FaTruck,
  FaShieldAlt,
  FaFacebook,
  FaTwitter,
  FaPinterest,
  FaEnvelope,
  FaMinus,
  FaPlus,
  FaBolt,
  FaRulerCombined
} from "react-icons/fa";

import { IoLogoWhatsapp } from "react-icons/io5";
import { getProductById } from '../../services/api';
import './ProductDetail.css';

import {
  FaMoneyBillWave,
  FaExchangeAlt,
  FaChevronDown,
  FaChevronUp
} from "react-icons/fa";

import RelatedProduct from './RelatedProduct';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const shareMenuRef = useRef(null);
  const [selectedSize, setSelectedSize] = useState('');
  const whatsappLink =
    "https://wa.me/923363788105?text=Hi,%20I'm%20interested%20in%20this%20product.";

  const [openAccordion, setOpenAccordion] = useState("details");

  const toggleAccordion = (section) => {
    setOpenAccordion(openAccordion === section ? "" : section);
  };

  const formatCategoryLabel = (category) => {
    if (!category) return '';
    return category
      .split(/[-_]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const [sizeGuide, setSizeGuide] = useState(null);
  const [showSizeGuideModal, setShowSizeGuideModal] = useState(false);

  const sizes = sizeGuide?.guide_rows?.map(r => r.size).filter(Boolean) || [];

  const toBulletList = (text, fallback) => {
    if (!text || !text.trim()) return fallback;
    return text.split('\n').map(line => line.trim()).filter(Boolean);
  };

  // --- Reviews ---
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewName, setReviewName] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  const loadReviews = async () => {
    const requestedId = id;
    setReviewsLoading(true);
    try {
      const response = await fetch(`https://demo-site.creativepulse.pk/demo-api/public/get-reviews.php?product_id=${requestedId}`);
      const data = await response.json();
      if (requestedId !== id) return;
      if (data.success) setReviews(data.reviews);
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      if (requestedId === id) setReviewsLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewName.trim()) {
      alert('Please enter your name.');
      return;
    }
    setSubmittingReview(true);
    try {
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/public/submit-review.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: id,
          customer_name: reviewName,
          rating: reviewRating,
          review_text: reviewText,
        })
      });
      const data = await response.json();
      if (data.success) {
        setReviewName('');
        setReviewRating(5);
        setReviewText('');
        setShowReviewForm(false);
        await loadReviews();
        await loadProduct(); // refresh aggregate rating/count shown at the top
      } else {
        alert(data.message || 'Failed to submit review');
      }
    } catch (error) {
      alert('Connection error');
    } finally {
      setSubmittingReview(false);
    }
  };

  const loadSizeGuide = async () => {
    const requestedId = id;
    try {
      const response = await fetch(`https://demo-site.creativepulse.pk/demo-api/public/get-product-guide.php?product_id=${requestedId}`);
      const data = await response.json();
      if (requestedId !== id) return;
      if (data.success && data.guide) {
        setSizeGuide(data.guide);
        const firstSize = data.guide.guide_rows?.[0]?.size;
        if (firstSize) setSelectedSize(firstSize);
      }
    } catch (error) {
      console.error('Error loading size guide:', error);
    }
  };

  useEffect(() => {
    setProduct(null);
    setSizeGuide(null);
    setSelectedSize('');
    setSelectedImage(0);
    setQuantity(1);
    setReviews([]);

    loadProduct();
    loadSizeGuide();
    loadReviews();

    const handleClickOutside = (event) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [id]);

  const loadProduct = async () => {
    const requestedId = id;
    setLoading(true);
    try {
      const data = await getProductById(requestedId);
      if (requestedId !== id) return;
      if (data.success && data.product) {
        setProduct(data.product);
      }
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      if (requestedId === id) setLoading(false);
    }
  };

  const getCurrentImage = () => {
    if (!product) return '';
    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      return product.images[selectedImage] || product.images[0];
    }
    if (product.image) return product.image;
    return 'https://via.placeholder.com/600x600?text=Product';
  };

  const getThumbnails = () => {
    if (!product) return [];
    if (product.images && Array.isArray(product.images) && product.images.length > 0) return product.images;
    if (product.image) return [product.image];
    return [];
  };

  const getCurrentPrice = () => parseFloat(product?.price || 0);

  const getCurrentSalePrice = () => {
    if (product?.sale_price) return parseFloat(product.sale_price);
    return null;
  };

  const getCurrentStock = () => product?.stock || 0;

  const addToCart = () => {
    const cart = JSON.parse(localStorage.getItem('cart')) || [];
    const effectivePrice = getCurrentSalePrice() || getCurrentPrice();
    const cartItem = {
      id: product.id,
      name: product.name,
      price: effectivePrice,
      quantity: quantity,
      image: getCurrentImage(),
      variant: selectedSize ? { size: selectedSize } : null
    };
    const existingItem = cart.find(item => item.id === cartItem.id && JSON.stringify(item.variant) === JSON.stringify(cartItem.variant));
    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      cart.push(cartItem);
    }
    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
    window.dispatchEvent(new Event('openSideCart'));
  };

  const buyNow = () => {
    addToCart();
    navigate('/cart');
  };

  const toggleWishlist = () => setIsWishlisted(!isWishlisted);
  const toggleShareMenu = () => setShowShareMenu(!showShareMenu);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(product?.name)}&url=${encodeURIComponent(window.location.href)}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(window.location.href)}&media=${getCurrentImage()}&description=${encodeURIComponent(product?.name)}`,
    email: `mailto:?subject=${encodeURIComponent(product?.name)}&body=${encodeURIComponent(window.location.href)}`
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    for (let i = 0; i < fullStars; i++) stars.push(<FaStar key={i} className="star filled" />);
    if (hasHalfStar) stars.push(<FaStarHalfAlt key="half" className="star half-filled" />);
    while (stars.length < 5) stars.push(<FaRegStar key={stars.length} className="star empty" />);
    return stars;
  };

  const [settings, setSettings] = useState({});

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await fetch('https://demo-site.creativepulse.pk/demo-api/public/get-settings.php');
        const data = await response.json();
        if (data.success && data.settings) {
          setSettings(data.settings);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    loadSettings();
  }, []);

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!product) return <div className="error-container">Product not found</div>;

  const thumbnails = getThumbnails();
  const currentImage = getCurrentImage();
  const rating = product.rating || 0;
  const currentPrice = getCurrentPrice();
  const currentSalePrice = getCurrentSalePrice();
  const displayPrice = currentSalePrice || currentPrice;
  const currentStock = getCurrentStock();
  const isOutOfStock = currentStock === 0 || product.status === 'out_of_stock';

  const detailsList = toBulletList(product.product_details, [
    'Regular fit',
    'Premium fabric blend',
    'Designed for comfort and durability',
    'Suitable for work, casual, and smart-casual styling'
  ]);
  const fabricList = toBulletList(product.fabric_care, [
    'Cotton blend fabric',
    'Machine wash cold',
    'Do not bleach',
    'Iron on medium heat',
    'Wash with similar colors'
  ]);
  const deliveryList = toBulletList(product.delivery_info, [
    'Standard delivery within 3–5 working days',
    'Free delivery on orders above PKR 4,000',
    'COD available nationwide'
  ]);
  const returnList = toBulletList(product.return_policy, [
    'Exchange available within 7 days',
    'Product must be unused with tags attached',
    'Sale items may have limited exchange eligibility'
  ]);

  return (
    <div className="product-detail-page">
      <div className="product-detail-container-wrap">
        <nav className="product-breadcrumb">
          <Link to="/">Home</Link>
          <span>&gt;</span>
          <Link to="/shop">Shop</Link>
          <span>&gt;</span>
          <Link to={`/shop?category=${product.category}`}>{formatCategoryLabel(product.category)}</Link>
          <span>&gt;</span>
          <span className="breadcrumb-current">{product.name}</span>
        </nav>
      </div>

      <div className="product-detail-container">
        <div className="product-gallery">
          {thumbnails.length > 1 && (
            <div className="thumbnail-scroll">
              <div className="thumbnail-list">
                {thumbnails.map((img, idx) => (
                  <div
                    key={idx}
                    className={`thumbnail-item ${selectedImage === idx ? 'active' : ''}`}
                    onClick={() => setSelectedImage(idx)}
                  >
                    <img src={img} alt={`${product.name} - ${idx + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="main-image">
            {product.featured === 1 && <span className="image-badge">New</span>}
            {currentSalePrice && <span className="image-badge sale-badge">Sale</span>}

            <div className="image-top-actions">
              <button
                className={`icon-circle-btn ${isWishlisted ? 'active' : ''}`}
                onClick={toggleWishlist}
                title="Wishlist"
              >
                <FaHeart />
              </button>
              <div className="share-wrapper" ref={shareMenuRef}>
                <button className="icon-circle-btn" onClick={toggleShareMenu} title="Share">
                  <FaShare />
                </button>
                {showShareMenu && (
                  <div className="share-dropdown">
                    <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer"><FaFacebook /> Facebook</a>
                    <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer"><FaTwitter /> Twitter</a>
                    <a href={shareLinks.pinterest} target="_blank" rel="noopener noreferrer"><FaPinterest /> Pinterest</a>
                    <a href={shareLinks.email}><FaEnvelope /> Email</a>
                  </div>
                )}
              </div>
            </div>

            <img src={currentImage} alt={product.name} />
          </div>
        </div>

        <div className="product-info">
          <div className="product-meta">
            <span className="product-category">{product.category?.toUpperCase()}</span>
          </div>

          <h1 className="product-title">{product.name}</h1>

          <div className="product-rating">
            <div className="stars">{renderStars(rating)}</div>
            <span className="rating-value">{rating.toFixed(1)}</span>
            <a href="#reviews-section" className="reviews-count">| {product.num_reviews || 0} reviews</a>
          </div>

          <div className="product-price-box">
            <div className="product-price">
              <span className="price-currency">PKR</span>
              <span className="price-amount">{displayPrice.toLocaleString()}</span>
            </div>
            {currentSalePrice && (
              <span className="price-old">PKR <del>{currentPrice.toLocaleString()}</del></span>
            )}
            {currentSalePrice && (
              <span className="discount-badge">
                -{Math.round(((currentPrice - currentSalePrice) / currentPrice) * 100)}% OFF
              </span>
            )}
          </div>

          <div className="product-short-desc">
            <p>{product.description?.substring(0, 150)}...</p>
          </div>

          {(sizes.length > 0 || sizeGuide) && (
            <div className="product-variants">
              <div className="variant-group">
                <div className="variant-label-row">
                  <label className="variant-label">Size</label>
                  {sizeGuide && (
                    <button type="button" className="size-guide-link" onClick={() => setShowSizeGuideModal(true)}>
                      <FaRulerCombined /> Size Guide
                    </button>
                  )}
                </div>
                {sizes.length > 0 && (
                  <div className="variant-options">
                    {sizes.map(size => (
                      <button
                        key={size}
                        className={`variant-option ${selectedSize === size ? 'active' : ''}`}
                        onClick={() => setSelectedSize(size)}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="stock-status">
            {product.status === 'draft' ? (
              <span className="out-stock-badge">Currently Unavailable</span>
            ) : currentStock > 0 ? (
              <span className="in-stock-badge">✓ In Stock ({currentStock} available)</span>
            ) : (
              <span className="out-stock-badge">✗ Out of Stock</span>
            )}
          </div>

          <label className="variant-label">Quantity</label>
          <div className="product-actions-row">
            <div className="quantity-selector">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={isOutOfStock}>
                <FaMinus />
              </button>
              <span>{quantity}</span>
              <button onClick={() => setQuantity(Math.min(currentStock, quantity + 1))} disabled={isOutOfStock}>
                <FaPlus />
              </button>
            </div>

            <button className="add-to-cart-btn" onClick={addToCart} disabled={isOutOfStock}>
              <FaShoppingBag /> Add to Cart
            </button>

            <div className="product-action-group">
              <button className="buy-now-btn" onClick={buyNow} disabled={isOutOfStock}>
                <FaBolt /> Buy Now
              </button>
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="whatsapp-btn">
                <IoLogoWhatsapp /> WhatsApp
              </a>
            </div>

            <p className="product-help-text">
              Need help with size or availability? <a href={whatsappLink} target="_blank" rel="noopener noreferrer">Chat with us</a>.
            </p>

            <div className="product-service-card">
              <div className="service-item">
                <div className="service-icon"><FaMoneyBillWave /></div>
                <div>
                  <h4>{settings.feature_cod_title || 'Cash on Delivery'}</h4>
                  <span>{settings.feature_cod_description || 'Pay when your order arrives'}</span>
                </div>
              </div>

              <div className="service-item">
                <div className="service-icon"><FaTruck /></div>
                <div>
                  <h4>{settings.feature_delivery_title || 'Nationwide Delivery'}</h4>
                  <span>{settings.feature_delivery_description || 'Shipping across Pakistan'}</span>
                </div>
              </div>

              <div className="service-item">
                <div className="service-icon"><FaShieldAlt /></div>
                <div>
                  <h4>{settings.feature_payment_title || 'Secure Payments'}</h4>
                  <span>{settings.feature_payment_description || 'Online payment supported'}</span>
                </div>
              </div>

              <div className="service-item">
                <div className="service-icon"><FaExchangeAlt /></div>
                <div>
                  <h4>{settings.feature_whatsapp_title || 'WhatsApp Support'}</h4>
                  <span>{settings.feature_whatsapp_description || 'Need help? Chat with us'}</span>
                </div>
              </div>
            </div>

            <div className="custom-product-accordion">
              <div className="accordion-item">
                <button className="accordion-header" onClick={() => toggleAccordion("details")}>
                  {product.product_details_title || 'Product Details'}
                  {openAccordion === "details" ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {openAccordion === "details" && (
                  <div className="accordion-body">
                    <ul>{detailsList.map((line, i) => <li key={i}>{line}</li>)}</ul>
                  </div>
                )}
              </div>

              <div className="accordion-item">
                <button className="accordion-header" onClick={() => toggleAccordion("fabric")}>
                  {product.fabric_care_title || 'Fabric & Care'}
                  {openAccordion === "fabric" ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {openAccordion === "fabric" && (
                  <div className="accordion-body">
                    <ul>{fabricList.map((line, i) => <li key={i}>{line}</li>)}</ul>
                  </div>
                )}
              </div>

              <div className="accordion-item">
                <button className="accordion-header" onClick={() => toggleAccordion("delivery")}>
                  {product.delivery_info_title || 'Delivery Information'}
                  {openAccordion === "delivery" ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {openAccordion === "delivery" && (
                  <div className="accordion-body">
                    <ul>{deliveryList.map((line, i) => <li key={i}>{line}</li>)}</ul>
                  </div>
                )}
              </div>

              <div className="accordion-item">
                <button className="accordion-header" onClick={() => toggleAccordion("return")}>
                  {product.return_policy_title || 'Return & Exchange Policy'}
                  {openAccordion === "return" ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {openAccordion === "return" && (
                  <div className="accordion-body">
                    <ul>{returnList.map((line, i) => <li key={i}>{line}</li>)}</ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================= Reviews Section ================= */}
      <div id="reviews-section" className="reviews-section">
        <div className="reviews-header">
          <div>
            <h2>Customer Reviews</h2>
            <div className="reviews-summary">
              <div className="stars">{renderStars(rating)}</div>
              <span className="rating-value">{rating.toFixed(1)}</span>
              <span className="reviews-count-text">based on {product.num_reviews || 0} review{product.num_reviews === 1 ? '' : 's'}</span>
            </div>
          </div>
          <button className="write-review-btn" onClick={() => setShowReviewForm(!showReviewForm)}>
            {showReviewForm ? 'Cancel' : 'Write a Review'}
          </button>
        </div>

        {showReviewForm && (
          <form className="review-form" onSubmit={handleSubmitReview}>
            <div className="review-form-row">
              <label>Your Name</label>
              <input type="text" value={reviewName} onChange={(e) => setReviewName(e.target.value)} required />
            </div>
            <div className="review-form-row">
              <label>Your Rating</label>
              <div className="review-star-picker">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    type="button"
                    key={n}
                    className={n <= reviewRating ? 'star-pick filled' : 'star-pick'}
                    onClick={() => setReviewRating(n)}
                  >
                    <FaStar />
                  </button>
                ))}
              </div>
            </div>
            <div className="review-form-row">
              <label>Your Review (optional)</label>
              <textarea rows="3" value={reviewText} onChange={(e) => setReviewText(e.target.value)} />
            </div>
            <button type="submit" className="submit-review-btn" disabled={submittingReview}>
              {submittingReview ? 'Submitting...' : 'Submit Review'}
            </button>
          </form>
        )}

        <div className="reviews-list">
          {reviewsLoading ? (
            <p className="reviews-loading">Loading reviews...</p>
          ) : reviews.length === 0 ? (
            <p className="reviews-empty">No reviews yet — be the first to review this product.</p>
          ) : (
            reviews.map(r => (
              <div key={r.id} className="review-card">
                <div className="review-card-avatar">
                  {r.customer_name?.charAt(0).toUpperCase() || '?'}
                </div>
                <div className="review-card-body">
                  <div className="review-card-header">
                    <strong>{r.customer_name}</strong>
                    <span className="review-date">{new Date(r.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                  </div>
                  <div className="stars review-card-stars">{renderStars(r.rating)}</div>
                  {r.review_text && <p className="review-text">{r.review_text}</p>}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <RelatedProduct />

      {showSizeGuideModal && sizeGuide && (
        <div className="size-guide-modal-overlay" onClick={() => setShowSizeGuideModal(false)}>
          <div className="size-guide-modal" onClick={(e) => e.stopPropagation()}>
            <div className="size-guide-modal-header">
              <div>
                <h2>{sizeGuide.guide_title}</h2>
                <p>{formatCategoryLabel(product.category)}</p>
              </div>
              <button onClick={() => setShowSizeGuideModal(false)}>×</button>
            </div>

            <div className="size-guide-modal-body">
              {sizeGuide.guide_image ? (
                <img src={sizeGuide.guide_image} alt="Size guide" className="size-guide-image" />
              ) : (
                <p className="size-guide-note">No size guide image has been uploaded for this product yet.</p>
              )}

              {sizeGuide.guide_rows?.length > 0 && (
                <div className="size-guide-chip-list">
                  {sizeGuide.guide_rows.map((row, i) => (
                    row.size && <span key={i} className="size-guide-chip">{row.size}</span>
                  ))}
                </div>
              )}

              {sizeGuide.guide_description && (
                <p className="size-guide-note">{sizeGuide.guide_description}</p>
              )}
            </div>

            <button className="size-guide-close-btn" onClick={() => setShowSizeGuideModal(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;